import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { getBatchStationMetrics } from '../services/api.service';

export interface EngineerStationData {
    id: string;
    name: string;
    lastMeasurement?: string | null;
    lastFlag?: string | null;
    minimumBattery?: number | null;
    maximumBattery?: number | null;
    temperatureMin?: number | null;
    temperatureMax?: number | null;
    windMax?: number | null;
    windMin?: number | null;
    radiationAvg?: number | null;
    radiationMax?: number | null;
    precipitationSum?: number | null;
    precipitationMax?: number | null;
    precipitationMin?: number | null;
    hasMultiplePrecipitationSensors?: boolean;
    precipitationSensors?: Record<string, {
        sum: number;
        max: number;
        min: number;
    }>;
    pressureMin?: number | null;
    pressureMax?: number | null;
    voltageMin?: number | null;
    voltageMax?: number | null;
    flaggedVariables?: string[];
    flaggedVariablesDetail?: Record<string, {
        count?: number;
        rc?: any;
        rs?: any;
        td?: any;
        ti?: any;
        ts?: any;
        mc?: any
    }>;
    fetchFailed?: boolean;
    failReason?: 'timeout' | 'error' | 'unfinished' | string;
}

interface StationMetricsResponse {
    stations: EngineerStationData[];
    loading?: boolean;
    cached?: boolean;
    message?: string;
}

export const useStationMetrics = (hours: number = 24) => {
    // useRef so the counter persists across renders but resets when `hours` changes
    const pollCountRef = useRef(0);

    const query = useQuery({
        queryKey: ['station-metrics', hours],
        queryFn: async () => {
            // Reset backoff counter each time a fresh query fires (hours changed or manual refetch)
            pollCountRef.current = 0;
            const result = await getBatchStationMetrics(hours);
            return result as StationMetricsResponse;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 3 * 60 * 60 * 1000, // 3 hours
        placeholderData: (previousData) => previousData,
        refetchInterval: (query) => {
            const data = query.state.data;

            // Data ready — slow background refresh every 5 minutes
            if (data?.stations && data.stations.length > 0 && !data.loading) {
                pollCountRef.current = 0;
                return 5 * 60 * 1000;
            }

            // Backend is generating the first payload (no stale data to show):
            // use exponential backoff: 2s, 4s, 8s, 16s, capped at 30s
            if (data?.loading && (!data.stations || data.stations.length === 0)) {
                pollCountRef.current += 1;
                const backoff = Math.min(2000 * 2 ** (pollCountRef.current - 1), 30000);
                return backoff;
            }

            // Refreshing stale data in background — poll every 10s
            if (data?.loading && (data.stations?.length ?? 0) > 0) {
                return 10000;
            }

            return 5 * 60 * 1000;
        },
        refetchOnWindowFocus: false,
        retry: 2,
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    });

    // Return enhanced data with proper loading state
    return {
        ...query,
        // Use the loading flag from the API response if available, otherwise fall back to query loading state
        isBackgroundLoading: query.data?.loading ?? false,
        hasData: (query.data?.stations?.length ?? 0) > 0,
        stations: query.data?.stations ?? [],
    };
};
